import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Building2, ArrowLeftRight, X } from "lucide-react";
import { useUser } from "@/context/UserContext";
import { useTenant } from "@/context/TenantContext";
import { useI18n } from "@/i18n/I18nContext";

// Aviso fijo para el super admin mientras actúa sobre una empresa concreta:
// todas las llamadas a la API van con X-Company-Override, así que tiene que
// saber en todo momento con qué empresa está trabajando y cómo salir.
const ManagingCompanyBanner = () => {
  const { user } = useUser();
  const { tenantOverride, setTenantOverride } = useTenant();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  if (user?.role !== "super_admin" || !tenantOverride) return null;

  const inAdmin = location.pathname.startsWith("/admin");

  const changeCompany = () => {
    setTenantOverride(null);
    navigate({ pathname: inAdmin ? location.pathname : "/admin/dashboard" });
  };

  const exitToGlobal = () => {
    setTenantOverride(null);
    if (inAdmin) {
      // En el panel, ?company= manda: si no se quita, la empresa vuelve a activarse.
      const params = new URLSearchParams(location.search);
      params.delete("company");
      const search = params.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
    }
  };

  return (
    <div
      data-testid="managing-company-banner"
      role="status"
      className="bg-amber-50 border-b border-amber-200 text-amber-900"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <Building2 className="h-4 w-4 shrink-0" />
        <span className="font-bold">{t("admin.managing_banner", { name: tenantOverride.name })}</span>
        <span className="hidden md:inline text-amber-800/80">{t("admin.managing_hint")}</span>
        <span className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            data-testid="managing-change"
            onClick={changeCompany}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-amber-300 bg-white/70 hover:bg-white font-semibold"
          >
            <ArrowLeftRight className="h-3.5 w-3.5" /> {t("admin.managing_change")}
          </button>
          <button
            type="button"
            data-testid="managing-exit"
            onClick={exitToGlobal}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-amber-300 bg-white/70 hover:bg-white font-semibold"
          >
            <X className="h-3.5 w-3.5" /> {t("admin.managing_exit")}
          </button>
        </span>
      </div>
    </div>
  );
};

export default ManagingCompanyBanner;
